import { useState } from 'react';
import useStepByStepAuthContext from '~/client/components/Auth/Authenticate/AuthSteps/context/hooks/useStepByStepAuthContext';
import useHandleLogin from '~/client/components/Auth/hooks/useHandleLogin';
import useNewDeviceNotification from '~/client/components/Auth/Login/LoginBody/FormLogin/useFormLogin/useNewDeviceNotification';
import useSms2fa from '~/client/components/Auth/Login/LoginBody/FormLogin/useFormLogin/useSms2fa';
import hasCredentialErrors from '~/client/components/Auth/Shared/utils/hasCredentialErrors';
import hasNewDeviceError from '~/client/components/Auth/Shared/utils/hasNewDeviceError';
import hasOtpErrors from '~/client/components/Auth/Shared/utils/hasOptErrors';
import hasSms2faError from '~/client/components/Auth/Shared/utils/hasSms2faError';
import updateAndOpenChat from '~/client/features/Comms/utils/updateAndOpenChat';
import useProto from '~/client/features/Proto/hooks/useProto';
import updateAndOpenChatProto from '~/client/features/Proto/utils/updateAndOpenChatProto';
import useConnectionClicked from '~/client/features/SocialAuth/SocialAuthLogin/SocialAuthLoginView/ConnectionButton/hooks/useConnectionClicked';
import usePhoneNumberHint from '~/client/hooks/usePhoneNumberHint';
import useLoginError from '~/client/hooks/user/useLoginError';
import useToast from '~/client/hooks/useToast';
import useAccountPopoverStore from '~/client/store/useAccountPopoverStore';
import deserializeErrors from '~/shared/utils/errors/deserializeErrors';
import AuthStepsEnum from '../../AuthStepsEnum';
import useLoginOrSignupSubmit from '../../hooks/helpers/useLoginOrSignupSubmit';

type Props = {
  isPopover?: boolean;
  onSuccess?: () => void;
};

const useSms2faCodeSubmit = ({ onSuccess, isPopover }: Props) => {
  const {
    setStep,
    loginCaptchaRequired: captchaRequired,
    isStubSms2fa,
    loginSuccessful,
    setLoginSuccessful,
    setShowInvalidCredentials,
    getValues,
    handleSubmit,
    errors,
    setError,
    setErrors,
    clearErrors,
    setValue,
  } = useStepByStepAuthContext();
  const { showNewDeviceNotification } = useNewDeviceNotification();
  const { isProtoEnabled } = useProto();

  const { dismissPopover } = useAccountPopoverStore();
  const { handleLogin, loading } = useHandleLogin({ captchaRequired });
  const { resetError } = useLoginError();
  const { socialAuthType } = useLoginOrSignupSubmit({});
  const handleConnectionClick = useConnectionClicked();
  const { setPhoneNumberHint } = usePhoneNumberHint();
  const { showToast } = useToast();
  const [needsSms2fa, setNeedsSms2fa] = useState(false);

  const [formValues, setFormValues] = useState<AuthFormValues>({
    username: getValues?.('username') ?? '',
    password: getValues?.('password') ?? '',
    captcha: getValues?.('captcha') ?? '',
    uses2FA: true,
  });

  const uses2FA = getValues?.('uses2FA');

  const submit = async (values: AuthFormValues, isSms2faStep?: boolean) => {
    clearErrors?.();
    resetError();

    const payload = await handleLogin(values);

    if (payload.errors?.length) {
      setErrors?.(payload.errors as GenericErrorType[]);
      setPhoneNumberHint(payload.errors);
      const deserializedErrors = deserializeErrors(
        payload.errors,
      ) as DeserializeError;
      if (hasNewDeviceError(deserializedErrors)) {
        showNewDeviceNotification();
      }
      const isOtp = !!deserializedErrors?.otp;
      const hasSms2fa = hasSms2faError(deserializedErrors);

      if (hasSms2fa) {
        setShowInvalidCredentials(true);
      }
      if (hasOtpErrors(deserializedErrors)) {
        setShowInvalidCredentials(true);
      }
      if (!isOtp) {
        setNeedsSms2fa(false);
      }
      if (hasCredentialErrors(deserializedErrors)) {
        setValue?.('password', '');
        setError?.('username', { message: payload.errors[0].message });
        setShowInvalidCredentials(true);
      }
      setValue?.('uses2FA', uses2FA || (isOtp && !hasSms2fa && !needsSms2fa));
      if (isSms2faStep && hasSms2fa) {
        setStep(AuthStepsEnum.EnterSms2fa);
        setShowInvalidCredentials(false);
      } else {
        setStep(AuthStepsEnum.EnterPassword);
        setShowInvalidCredentials(true);
      }
      return;
    }

    const userData = payload.userManagement;

    if (isProtoEnabled) {
      updateAndOpenChatProto();
    } else {
      updateAndOpenChat({
        opts: {
          customer: {
            externalId: userData?.currentProfile?._id,
            name: userData?.currentUser?.username,
            email: userData?.currentUser?.email,
          },
        },
      });
    }

    onSuccess?.();
    setLoginSuccessful(true);
    if (isPopover) {
      dismissPopover(true);
    }
  };

  const handleCodeSubmit = (otp?: string) => {
    if (loading || loginSuccessful) {
      return;
    }
    const values = {
      ...formValues,
      otp,
    };
    if (socialAuthType) {
      resetError();
      handleConnectionClick({
        handle: socialAuthType as Connection,
        otp,
        setOtpError,
      });
    } else {
      submit({ ...values });
    }
  };
  const setOtpError = () => {
    setErrors?.([
      { message: errors?.otp?.message ?? '' },
    ] as GenericErrorType[]);
  };

  const handleCodeSubmitStub = async (_?: string) => {
    setError?.('username', { message: 'INVALID_USERNAME_OR_PASSWORD' });
    setShowInvalidCredentials(true);
    setStep(AuthStepsEnum.EnterPassword);
  };

  const { authCodeErrorMessage } = useSms2fa(errors ?? {}, submit, formValues);

  return {
    authCodeErrorMessage,
    needsSms2fa,
    uses2FA,
    handleCodeSubmit: isStubSms2fa ? handleCodeSubmitStub : handleCodeSubmit,
    handleSubmit: handleSubmit(async (values) => {
      setFormValues(values);
      submit(values);
    }),
    handleResendCodeClick: () => {
      submit(formValues, true);
      setStep(AuthStepsEnum.EnterSms2fa);
      showToast({
        message: 'profile.new_code_sent',
        timeout: 6000,
        variant: 'success',
        position: 'bottom',
      });
    },
  };
};

export default useSms2faCodeSubmit;
